import { logs, SeverityNumber } from "@opentelemetry/api-logs";
import type { AnyValueMap } from "@opentelemetry/api-logs";

// Resolve the Logger PER EMIT, never at module scope. api-logs' getLogger
// consults the globalThis registry on every call, but a Logger obtained
// BEFORE setGlobalLoggerProvider() is a bundle-local ProxyLogger whose
// delegate is only upgraded by the api-logs COPY that performed the
// registration — and Next bundles api-logs separately into the
// instrumentation bundle and every route bundle. A route bundle whose module
// graph evaluated before instrumentation finished registering therefore
// cached a permanently-noop logger: whether a given cold start lost that
// race decided whether the whole instance's logs reached SigNoz
// (2026-06-10 diagnosis — instances alternated between full delivery and
// total silence on identical builds). Per-emit resolution is cheap: the SDK
// LoggerProvider caches loggers by name+version internally.
const otelLogger = () => logs.getLogger("@atriam/observability", "1.0.0");

// Attention-only filtering: only emit OTel log records at WARN+ severity.
// INFO/DEBUG still mirror to console (visible in `docker logs`) but never
// reach SigNoz. Drops 95%+ of log volume while keeping local DX intact.
//   SeverityNumber.DEBUG = 5, INFO = 9, WARN = 13, ERROR = 17
//
// Escape hatch: callers can mark an INFO/DEBUG log as audit-class by passing
// `{ audit: true, ... }` in meta. Those records bypass the floor so things
// like "webhook accepted", "pipeline tick processed", "payment captured"
// remain queryable in SigNoz without flooding the pipeline with all info
// logs. The `audit` key is stripped from the attribute set before emit so it
// doesn't pollute SigNoz columns; it only changes the gate decision.
export const OTEL_MIN_SEVERITY: SeverityNumber = SeverityNumber.WARN;

type Meta = Record<string, unknown> | Error | undefined;

function isAuditMeta(meta: unknown): boolean {
  return (
    !!meta &&
    typeof meta === "object" &&
    !(meta instanceof Error) &&
    (meta as { audit?: unknown }).audit === true
  );
}

function stripAuditMarker(meta: Meta): Meta {
  if (isAuditMeta(meta)) {
    const { audit: _audit, ...rest } = meta as Record<string, unknown>;
    return rest;
  }
  return meta;
}

// Dedup key includes a stable subset of meta — workspace_id / user_id / route
// where present. Without this, two tenants hitting the same retry storm would
// collapse onto one bucket and the second tenant's first error would be
// suppressed (counted as another occurrence of the first). Console mirror is
// never throttled either way.
const DEDUP_CONTEXT_KEYS: readonly string[] = [
  "workspaceId",
  "workspace_id",
  "userId",
  "user_id",
  "route",
  "path",
  "label",
];

function dedupContextSlug(meta: Meta): string {
  if (!meta || meta instanceof Error || typeof meta !== "object") {
    return "";
  }
  const parts: string[] = [];
  const record = meta;
  for (const key of DEDUP_CONTEXT_KEYS) {
    const v = record[key];
    if (typeof v === "string" || typeof v === "number") {
      parts.push(`${key}=${String(v)}`);
    }
  }
  return parts.length === 0 ? "" : `|${parts.join("&")}`;
}

// Per-key dedup window. A repeated (severity, message) pair within this window
// is suppressed on the OTel emit path; the next allowed emission carries a
// _dedup.suppressed_count attribute summarising the gap. Console is never
// throttled — developer visibility wins over noise locally.
//
// Why: any tight loop that calls logger.warn/error on every event (failed
// WebRTC reconnect, dead realtime channel, retry storm) used to ship 80+
// records per second to SigNoz, blowing past the BatchLogRecordProcessor
// queue and silently dropping records. 1s/key keeps the signal — first
// occurrence is always emitted — while capping volume at ~1 rps per body.
//
// Audit-class records (`{ audit: true }`) skip the window: they are
// low-volume by contract and each one is a distinct event. Two calls in one
// workspace inside 1s used to collapse their `[CallMilestone]` lines onto one
// bucket, losing the second call's milestone to `_dedup.suppressed_count`.
const OTEL_DEDUP_WINDOW_MS = 1_000;
const AUDIT_BYPASS = { emit: true, suppressedCount: 0 } as const;
const OTEL_DEDUP_MAX_KEYS = 200;
const MAX_ATTRIBUTE_STRING_LENGTH = 2_048;
const MAX_ATTRIBUTE_ARRAY_LENGTH = 50;
const MAX_ATTRIBUTE_KEYS = 50;
const MAX_ATTRIBUTE_DEPTH = 4;
const MAX_ATTRIBUTE_PAYLOAD_CHARS = 12_000;

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const BEARER_PATTERN = /\bBearer\s+[^\s,;]+/gi;
const SECRET_QUERY_PATTERN =
  /([?&](?:access_token|refresh_token|id_token|api_key|token|key)=)[^&#\s]+/gi;

function isSensitiveAttributeKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[.\-\s]/g, "_");
  return (
    normalized === "authorization" ||
    normalized === "cookie" ||
    normalized === "set_cookie" ||
    normalized === "password" ||
    normalized === "passphrase" ||
    normalized === "access_token" ||
    normalized === "refresh_token" ||
    normalized === "id_token" ||
    normalized === "api_key" ||
    normalized === "private_key" ||
    normalized === "client_secret" ||
    normalized.endsWith("_password") ||
    normalized.endsWith("_secret") ||
    normalized.endsWith("_credential")
  );
}

interface SanitizationBudget {
  remaining: number;
}

function sanitizeString(value: string, budget?: SanitizationBudget): string {
  const redacted = value
    .replace(EMAIL_PATTERN, "[REDACTED_EMAIL]")
    .replace(BEARER_PATTERN, "Bearer [REDACTED]")
    .replace(SECRET_QUERY_PATTERN, "$1[REDACTED]");
  const available = Math.min(
    MAX_ATTRIBUTE_STRING_LENGTH,
    budget?.remaining ?? MAX_ATTRIBUTE_STRING_LENGTH,
  );
  let result: string;
  if (redacted.length <= available) {
    result = redacted;
  } else if (available <= 12) {
    result = redacted.slice(0, Math.max(0, available));
  } else {
    result = `${redacted.slice(0, available - 12)}[TRUNCATED]`;
  }
  if (budget) {
    budget.remaining = Math.max(0, budget.remaining - result.length);
  }
  return result;
}

function sanitizeAttributeValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
  budget: SanitizationBudget,
): unknown {
  if (typeof value === "string") {
    return sanitizeString(value, budget);
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  ) {
    return value;
  }
  if (typeof value === "bigint") {
    return sanitizeString(String(value), budget);
  }
  if (
    value === undefined ||
    typeof value === "function" ||
    typeof value === "symbol"
  ) {
    return undefined;
  }
  if (depth >= MAX_ATTRIBUTE_DEPTH) {
    return sanitizeString("[MAX_DEPTH]", budget);
  }
  if (seen.has(value)) {
    return sanitizeString("[CIRCULAR]", budget);
  }
  seen.add(value);

  if (Array.isArray(value)) {
    const result: unknown[] = [];
    for (const item of value.slice(0, MAX_ATTRIBUTE_ARRAY_LENGTH)) {
      if (budget.remaining <= 0) {
        break;
      }
      const sanitized = sanitizeAttributeValue(item, depth + 1, seen, budget);
      if (sanitized !== undefined) {
        result.push(sanitized);
      }
    }
    return result;
  }

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value).slice(
    0,
    MAX_ATTRIBUTE_KEYS,
  )) {
    if (budget.remaining <= 0) {
      break;
    }
    if (isSensitiveAttributeKey(key)) {
      continue;
    }
    budget.remaining = Math.max(0, budget.remaining - key.length - 4);
    const sanitized = sanitizeAttributeValue(item, depth + 1, seen, budget);
    if (sanitized !== undefined) {
      result[key] = sanitized;
    }
  }
  return result;
}

function sanitizeAttributes(
  attributes: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = sanitizeAttributeValue(attributes, 0, new WeakSet(), {
    remaining: MAX_ATTRIBUTE_PAYLOAD_CHARS,
  });
  return sanitized && typeof sanitized === "object" && !Array.isArray(sanitized)
    ? (sanitized as Record<string, unknown>)
    : {};
}

interface DedupBucket {
  lastEmitAt: number;
  suppressedSinceLast: number;
}

const dedupBuckets = new Map<string, DedupBucket>();

function dedupCheckAndUpdate(key: string): {
  emit: boolean;
  suppressedCount: number;
} {
  const now = Date.now();
  const bucket = dedupBuckets.get(key);

  if (!bucket || now - bucket.lastEmitAt >= OTEL_DEDUP_WINDOW_MS) {
    const suppressed = bucket?.suppressedSinceLast ?? 0;
    dedupBuckets.set(key, { lastEmitAt: now, suppressedSinceLast: 0 });
    if (dedupBuckets.size > OTEL_DEDUP_MAX_KEYS) {
      evictStaleBuckets(now);
    }
    return { emit: true, suppressedCount: suppressed };
  }

  bucket.suppressedSinceLast += 1;
  return { emit: false, suppressedCount: 0 };
}

function evictStaleBuckets(now: number): void {
  const cutoff = now - OTEL_DEDUP_WINDOW_MS * 60; // 60 windows = 1min
  for (const [key, bucket] of dedupBuckets) {
    if (bucket.lastEmitAt < cutoff) {
      dedupBuckets.delete(key);
    }
  }
}

function consoleFor(severity: SeverityNumber) {
  switch (severity) {
    case SeverityNumber.ERROR:
      return console.error;
    case SeverityNumber.WARN:
      return console.warn;
    default:
      return console.log;
  }
}

// Suppress the console mirror in production browser builds. The meta object
// commonly carries workspace_id / user_id / raw error objects — none of which
// should be visible to anyone with DevTools open in a customer's browser. The
// node-side log path is unaffected (SigNoz still gets the structured record).
function shouldMirrorToConsole(): boolean {
  // Use a globalThis membership test rather than `typeof window` so node/bun
  // consumers (the gateways) typecheck this isomorphic module without pulling
  // in the DOM lib. Behaviour is identical: a browser has `window` → keep
  // going; node/bun has none → mirror to console.
  const inBrowser = typeof globalThis !== "undefined" && "window" in globalThis;
  if (!inBrowser) {
    return true;
  }
  if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
    return true;
  }
  return false;
}

/**
 * OTel's AnyValue accepts only primitives, arrays of primitives, and nested
 * primitive maps — an Error (or DOMException) assigned under a meta key is none
 * of those, so the exporter silently DROPS the whole attribute. Every
 * `logger.warn("x", { error: err })` then reached SigNoz / the browser pipeline
 * with no cause (2026-06-23: the uplink-watchdog "mic re-acquire failed" logged
 * a blank `attempt: N`, no DOMException name). Flatten any Error-shaped value
 * into primitive sibling keys (`<key>.name` / `.message` / `.stack` / `.code`)
 * so the canonical `{ error: err }` / `{ err }` pattern survives the bridge.
 * DOMException isn't reliably `instanceof Error`, so duck-type on string
 * name+message.
 *
 * A PostgREST `{ data, error }` error is a PLAIN OBJECT
 * (`{ message, code, details, hint }`, no `name`), so it is recognised by its
 * `details`/`hint` keys and flattened the same way.
 *
 * The `cause` chain is walked too (depth 5, mirroring `flattenCauseChain` on
 * the PostHog path): `<key>.cause_messages` joins every cause's message and
 * the immediate cause's code/details/hint/status land on `<key>.cause.*`.
 * WARN never reaches PostHog, so before this a
 * `logger.warn("x", { error: new Error("x_failed", { cause: pgErr }) })`
 * recorded the wrapper label and dropped the real Postgres error everywhere.
 */
interface ErrorLike {
  name?: unknown;
  message?: unknown;
  stack?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
  status?: unknown;
  cause?: unknown;
}

const MAX_CAUSE_DEPTH = 5;

function isErrorLike(value: unknown): value is ErrorLike {
  if (value instanceof Error) {
    return true;
  }
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (typeof (value as ErrorLike).message !== "string") {
    return false;
  }
  return (
    typeof (value as ErrorLike).name === "string" ||
    "details" in value ||
    "hint" in value
  );
}

// Any value carrying a string `message` is a usable cause, Error or not —
// same rule as `asCause` in apps/web/lib/analytics/flatten-cause-chain.ts.
function asCause(value: unknown): ErrorLike | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  return typeof (value as ErrorLike).message === "string" ? value : null;
}

function setPgFields(
  attributes: Record<string, unknown>,
  prefix: string,
  e: ErrorLike,
): void {
  if (typeof e.code === "string" || typeof e.code === "number") {
    attributes[`${prefix}.code`] = e.code;
  }
  if (typeof e.details === "string" && e.details) {
    attributes[`${prefix}.details`] = e.details;
  }
  if (typeof e.hint === "string" && e.hint) {
    attributes[`${prefix}.hint`] = e.hint;
  }
  if (typeof e.status === "number") {
    attributes[`${prefix}.status`] = e.status;
  }
}

function flattenError(
  attributes: Record<string, unknown>,
  key: string,
  e: ErrorLike,
): void {
  if (typeof e.name === "string") {
    attributes[`${key}.name`] = e.name;
  }
  if (typeof e.message === "string") {
    attributes[`${key}.message`] = e.message;
  }
  if (typeof e.stack === "string") {
    attributes[`${key}.stack`] = e.stack;
  }
  setPgFields(attributes, key, e);

  const chain: ErrorLike[] = [];
  for (
    let node = asCause(e.cause);
    node && chain.length < MAX_CAUSE_DEPTH;
    node = asCause(node.cause)
  ) {
    chain.push(node);
  }
  if (chain.length === 0) {
    return;
  }
  attributes[`${key}.cause_messages`] = chain
    .map((c) => String(c.message))
    .join(" | ");
  setPgFields(attributes, `${key}.cause`, chain[0]);
}

function flattenErrorValues(attributes: Record<string, unknown>): void {
  for (const key of Object.keys(attributes)) {
    const value = attributes[key];
    if (!isErrorLike(value)) {
      continue;
    }
    delete attributes[key];
    flattenError(attributes, key, value);
  }
}

function emit(
  severity: SeverityNumber,
  severityText: string,
  message: string,
  meta?: Meta,
) {
  const audit = isAuditMeta(meta);
  const cleanMeta = stripAuditMarker(meta);
  const attributes: Record<string, unknown> = {};
  if (cleanMeta instanceof Error) {
    flattenError(attributes, "error", cleanMeta);
  } else if (cleanMeta && typeof cleanMeta === "object") {
    Object.assign(attributes, cleanMeta);
  }
  flattenErrorValues(attributes);
  const safeAttributes = sanitizeAttributes(attributes);
  const safeMessage = sanitizeString(message);

  // Attention-only: only emit OTel log records at WARN+. INFO/DEBUG stay
  // console-only — unless the caller passed `{ audit: true, ... }`, in which
  // case the record bypasses the floor so audit-class events (webhook
  // accepted, pipeline tick processed, payment captured) remain queryable in
  // SigNoz.
  if (audit || severity >= OTEL_MIN_SEVERITY) {
    if (audit) {
      safeAttributes["audit"] = true;
    }
    const { emit: shouldEmit, suppressedCount } = audit
      ? AUDIT_BYPASS
      : dedupCheckAndUpdate(
          `${severityText}:${safeMessage}${dedupContextSlug(cleanMeta)}`,
        );
    if (shouldEmit) {
      if (suppressedCount > 0) {
        safeAttributes["_dedup.suppressed_count"] = suppressedCount;
      }
      otelLogger().emit({
        severityNumber: severity,
        severityText,
        body: safeMessage,
        attributes: safeAttributes as AnyValueMap,
      });
    }
  }

  // Console mirror. Suppressed in production browser builds — leaving
  // arbitrary meta in the console exposes workspace_id / user_id / raw error
  // objects to anyone with DevTools open. Local dev and node-side logs are
  // unaffected.
  if (shouldMirrorToConsole()) {
    const fn = consoleFor(severity);
    if (cleanMeta === undefined) {
      fn(message);
    } else {
      fn(message, cleanMeta);
    }
  }
}

function emitVariadic(
  severity: SeverityNumber,
  severityText: string,
  message: string,
  args: unknown[],
) {
  // Service-side variadic logger. Audit-marker can ride on the first object
  // arg the same way `emit()` handles meta on the standard logger.
  const firstArg = args[0];
  const audit =
    args.length === 1 &&
    firstArg &&
    typeof firstArg === "object" &&
    !(firstArg instanceof Error)
      ? (firstArg as { audit?: unknown }).audit === true
      : false;
  const cleanFirst = audit
    ? (() => {
        const { audit: _audit, ...rest } = firstArg as Record<string, unknown>;
        return rest;
      })()
    : firstArg;
  const cleanArgs = audit ? [cleanFirst, ...args.slice(1)] : args;

  const attributes: Record<string, unknown> = {};
  if (
    cleanArgs.length === 1 &&
    cleanArgs[0] &&
    typeof cleanArgs[0] === "object"
  ) {
    if (cleanArgs[0] instanceof Error) {
      flattenError(attributes, "error", cleanArgs[0]);
    } else {
      Object.assign(attributes, cleanArgs[0] as Record<string, unknown>);
    }
  } else if (cleanArgs.length > 0) {
    attributes.args = cleanArgs;
  }
  flattenErrorValues(attributes);
  const safeAttributes = sanitizeAttributes(attributes);
  const safeMessage = sanitizeString(message);

  if (audit || severity >= OTEL_MIN_SEVERITY) {
    if (audit) {
      safeAttributes["audit"] = true;
    }
    const dedupMeta =
      cleanArgs.length === 1 && cleanArgs[0] && typeof cleanArgs[0] === "object"
        ? (cleanArgs[0] as Record<string, unknown>)
        : undefined;
    const { emit: shouldEmit, suppressedCount } = audit
      ? AUDIT_BYPASS
      : dedupCheckAndUpdate(
          `${severityText}:${safeMessage}${dedupContextSlug(dedupMeta)}`,
        );
    if (shouldEmit) {
      if (suppressedCount > 0) {
        safeAttributes["_dedup.suppressed_count"] = suppressedCount;
      }
      otelLogger().emit({
        severityNumber: severity,
        severityText,
        body: safeMessage,
        attributes: safeAttributes as AnyValueMap,
      });
    }
  }

  if (shouldMirrorToConsole()) {
    const fn = consoleFor(severity);
    if (cleanArgs.length === 0) {
      fn(message);
    } else {
      fn(message, ...cleanArgs);
    }
  }
}

export interface Logger {
  error(message: string, meta?: Meta): void;
  warn(message: string, meta?: Record<string, unknown>): void;
  info(message: string, meta?: Record<string, unknown>): void;
  debug(message: string, meta?: Record<string, unknown>): void;
}

export const logger: Logger = {
  error: (message, meta) => emit(SeverityNumber.ERROR, "ERROR", message, meta),
  warn: (message, meta) => emit(SeverityNumber.WARN, "WARN", message, meta),
  info: (message, meta) => emit(SeverityNumber.INFO, "INFO", message, meta),
  debug: (message, meta) => emit(SeverityNumber.DEBUG, "DEBUG", message, meta),
};

// Service logger API used by backend services:
//   serviceLogger.error("ModuleName", "msg", { ... })
export interface ServiceLogger {
  error(module: string, message: string, ...args: unknown[]): void;
  warn(module: string, message: string, ...args: unknown[]): void;
  info(module: string, message: string, ...args: unknown[]): void;
  debug(module: string, message: string, ...args: unknown[]): void;
}

function buildServiceLog(severity: SeverityNumber, severityText: string) {
  return (module: string, message: string, ...args: unknown[]) => {
    // Body carries the real message (`[module] message`) so SigNoz can search
    // it by text. Previously body was just `[module]` and the message was
    // buried in attributes.args — every gateway error looked identical
    // (`[telephony-gateway]`) and the dedup key collapsed distinct messages
    // from the same module onto one bucket. Trailing args (the context object)
    // still flow through as flat attributes via emitVariadic.
    const body = `[${module}] ${message}`;
    emitVariadic(severity, severityText, body, args);
  };
}

export function createServiceLogger(): ServiceLogger {
  return {
    error: buildServiceLog(SeverityNumber.ERROR, "ERROR"),
    warn: buildServiceLog(SeverityNumber.WARN, "WARN"),
    info: buildServiceLog(SeverityNumber.INFO, "INFO"),
    debug: buildServiceLog(SeverityNumber.DEBUG, "DEBUG"),
  };
}

// Test-only hook: reset dedup state so unit tests can exercise the rate
// limiter deterministically without leaking buckets across cases.
export function __resetDedupForTests(): void {
  dedupBuckets.clear();
}
